/**
 * Persistent "don't offer the first-visitor coupon again" flag for this browser.
 *
 * Functional storage (a UI preference, no identifier), so it needs no consent and cookie-consent.ts never clears it.
 * Set only on an explicit action: the visitor got a coupon ("done") or clicked "not interested" ("dismissed").
 * Being logged in does not set it (shared computers); the server already hides the popup for logged-in visitors.
 * Closing the popup with X keeps the per-session behaviour only.
 */
const STORAGE_KEY = 'ts-first-visitor-popup';

export type FirstVisitorSuppressionState = 'done' | 'dismissed';

export function isFirstVisitorPopupSuppressed(): boolean {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const state = (JSON.parse(raw) as { state?: string })?.state;
        return state === 'done' || state === 'dismissed';
    } catch {
        // Private mode / blocked storage / malformed value: fall back to the per-session flag.
        return false;
    }
}

export function suppressFirstVisitorPopup(state: FirstVisitorSuppressionState): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
        // Storage unavailable: the session flag still hides it for this session.
    }
}
